import './index.css'

import type { PlaybackLocation } from '@disco/state-management/selectors/media'
import React from 'react'
import type { TrackTextSettings } from 'types/business'
import type { GroupId } from 'types/group'
import type { Track, TrackId } from 'types/track'
import type { TrackLocation } from 'types/ui'
import TrackInfo from 'components/common/track-info'
import type { PaneItemAppearance } from 'components/ui/pane-item'
import PaneItem from 'components/ui/pane-item'
import getTrackStatus from 'components/utils/track-status'
type Props = {
  actions?: React.ReactNode
  appearance?: PaneItemAppearance
  className?: string
  dataTestId?: string
  groupId?: GroupId | null | undefined
  hideWatermarkingFlag?: boolean
  itemIndex?: number
  location: TrackLocation
  paneIndex?: number
  showIndex?: boolean
  sourcePlaylistId?: number | null | undefined
  sourceShortlistId?: string
  track: Track
  tracklist?: TrackId[] | null | undefined
  queue?: boolean
  onClick?: (...args: Array<any>) => any
  nodeRight?: React.ReactNode
  textSettings?: TrackTextSettings
  playbackLocation?: PlaybackLocation | null
}

/**
 * TrackItem component for displaying individual track items in lists
 *
 * A functional component that wraps track information in a standardized pane item
 * format. Provides track status indicators, territory restrictions, optional
 * indexing, and click handling for track interactions.
 *
 * @param props - Component props
 * @param props.actions - Optional action buttons or elements for the track
 * @param props.appearance - Visual appearance variant for the pane item
 * @param props.groupId - Group context for the track
 * @param props.hideWatermarkingFlag - Whether to hide watermarking indicators
 * @param props.itemIndex - Position index of the item in a list
 * @param props.location - Current location context for the track
 * @param props.paneIndex - Index of the pane containing this item
 * @param props.showIndex - Whether to display the item index
 * @param props.sourcePlaylistId - ID of the playlist this track comes from
 * @param props.sourceShortlistId - ID of the shortlist this track comes from
 * @param props.track - Track data object
 * @param props.tracklist - Array of track IDs for playlist context
 * @param props.queue - Whether this item is in a queue context
 * @param props.onClick - Click handler for the track item
 * @param props.nodeRight - Content to display on the right side
 * @param props.textSettings - Text display configuration settings
 * @returns JSX element containing the formatted track item
 */
const TrackItem = ({
  actions,
  appearance,
  className,
  dataTestId,
  groupId,
  hideWatermarkingFlag,
  itemIndex,
  location,
  paneIndex,
  showIndex,
  sourcePlaylistId,
  sourceShortlistId,
  track,
  tracklist,
  queue,
  onClick,
  nodeRight,
  textSettings,
  playbackLocation,
}: Props): React.ReactNode => {
  const { bar, noun } = getTrackStatus(track)
  return (
    <PaneItem
      appearance={appearance}
      className={className}
      dataTestId={dataTestId}
      index={showIndex && itemIndex !== undefined ? itemIndex + 1 : undefined}
      content={
        <TrackInfo
          groupId={groupId}
          hideWatermarkingFlag={hideWatermarkingFlag}
          location={location}
          paneIndex={paneIndex}
          sourcePlaylistId={sourcePlaylistId}
          sourceShortlistId={sourceShortlistId}
          track={track}
          tracklist={tracklist}
          queue={queue}
          textSettings={textSettings}
          playbackLocation={playbackLocation}
        />
      }
      actions={actions}
      nodeRight={nodeRight}
      bar={bar}
      tooltip={
        track.outside_your_territories
          ? `This ${noun} is not available in your territory`
          : null
      }
      disabled={track.outside_your_territories && location != 'shortlist'}
      onClick={onClick}
    />
  )
}

export default TrackItem
